import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Scanner } from "@yudiel/react-qr-scanner";
import { Dialog } from "@headlessui/react";
import {
  XMarkIcon,
  CameraIcon,
  DocumentTextIcon,
  CheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { RootState } from "../../store";
import {
  setQRScanResult,
} from "../../feature/orders/orderSlice";
import { MANUFACTURING_STAGES, ManufacturingItemType } from "../../feature/orders/order";

interface QRScannerProps {
  isOpen: boolean;
  onClose: () => void;
}

const QRScanner: React.FC<QRScannerProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { qrScanResult, loading } = useSelector(
    (state: RootState) => state?.orders || []
  );

  const [scannerActive, setScannerActive] = useState(true);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedNextStage, setSelectedNextStage] = useState("");
  const [updateNotes, setUpdateNotes] = useState("");
  const [updatePhoto, setUpdatePhoto] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleScan = (result: string) => {
    if (result) {
      setScannerActive(false);
      // dispatch(findItemByQR(result));
    }
  };

  const handleError = (error: unknown) => {
    console.error("QR Scanner Error:", error);
  };

  const getAvailableNextStages = (
    currentStage: string,
    itemType: ManufacturingItemType
  ): string[] => {
    const stages = MANUFACTURING_STAGES[itemType];
    const currentIndex = stages.indexOf(currentStage);

    if (currentIndex === -1) {
      // If current stage not found, allow first stage
      return [stages[0]];
    }

    // Allow next stage(s)
    const nextStages = [];
    if (currentIndex + 1 < stages.length) {
      nextStages.push(stages[currentIndex + 1]);
    }

    return nextStages;
  };

  const handleUpdateStage = async () => {
    if (!qrScanResult || !selectedNextStage) return;

    const updateData = {
      orderId: qrScanResult.order.id,
      productId: qrScanResult.product.id,
      itemId: qrScanResult.item.id,
      newStage: selectedNextStage,
      userId: "current-user", // This should come from auth
      notes: updateNotes,
      photoUrl: updatePhoto ? URL.createObjectURL(updatePhoto) : undefined,
    };

    // dispatch(updateItemStage(updateData));

    // Reset form
    setSelectedNextStage("");
    setUpdateNotes("");
    setUpdatePhoto(null);
    setShowUpdateModal(false);
    // dispatch(setQRScanResult(null));
    onClose();
  };

  const handlePhotoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUpdatePhoto(file);
    }
  };

  const getStageStatusColor = (
    stage: string,
    currentStage: string,
    itemType: ManufacturingItemType
  ) => {
    const stages = MANUFACTURING_STAGES[itemType];
    const stageIndex = stages.indexOf(stage);
    const currentIndex = stages.indexOf(currentStage);

    if (stageIndex < currentIndex) {
      return "text-green-600 bg-green-100"; // Completed
    } else if (stageIndex === currentIndex) {
      return "text-blue-600 bg-blue-100"; // Current
    } else {
      return "text-gray-400 bg-gray-100"; // Pending
    }
  };

  const getStageIcon = (
    stage: string,
    currentStage: string,
    itemType: ManufacturingItemType
  ) => {
    const stages = MANUFACTURING_STAGES[itemType];
    const stageIndex = stages.indexOf(stage);
    const currentIndex = stages.indexOf(currentStage);

    if (stageIndex < currentIndex) {
      return <CheckIcon className="w-4 h-4" />;
    } else if (stageIndex === currentIndex) {
      return <ClockIcon className="w-4 h-4" />;
    } else {
      return <div className="w-4 h-4 rounded-full border border-gray-300" />;
    }
  };

  const resetScanner = () => {
    setScannerActive(true);
    dispatch(setQRScanResult(null));
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md w-full rounded-xl bg-white overflow-hidden">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <Dialog.Title className="text-xl font-semibold">
                QR Code Scanner
              </Dialog.Title>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {scannerActive && !qrScanResult && (
              <div className="space-y-4">
                <p className="text-gray-600 text-sm">
                  Point your camera at a QR code to scan an item
                </p>
                <div className="aspect-square w-full max-w-sm mx-auto rounded-lg overflow-hidden">
                  <Scanner
                    onScan={(result) => handleScan(result[0]?.rawValue || "")}
                    onError={handleError}
                    constraints={{
                      facingMode: "environment",
                    }}
                  />
                </div>
              </div>
            )}

            {qrScanResult && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-lg mb-2">Item Found!</h3>

                  <div className="space-y-2 text-sm">
                    <p>
                      <strong>Item Type:</strong> {qrScanResult.item.type}
                    </p>
                    <p>
                      <strong>Current Stage:</strong>{" "}
                      {qrScanResult.item.currentStage}
                    </p>
                    <p>
                      <strong>Status:</strong>
                      <span
                        className={`ml-2 px-2 py-1 rounded text-xs ${
                          qrScanResult.item.status === "completed"
                            ? "bg-green-100 text-green-800"
                            : qrScanResult.item.status === "in_progress"
                            ? "bg-blue-100 text-blue-800"
                            : qrScanResult.item.status === "dispatched"
                            ? "bg-purple-100 text-purple-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {qrScanResult.item.status}
                      </span>
                    </p>
                    <p>
                      <strong>Customer:</strong>{" "}
                      {qrScanResult.order.customer.name}
                    </p>
                    <p>
                      <strong>Product:</strong> {qrScanResult.product.name}
                    </p>
                  </div>

                  {/* Workflow Progress */}
                  <div className="mt-4">
                    <h4 className="font-medium mb-2">Manufacturing Progress</h4>
                    <div className="space-y-1">
                      {MANUFACTURING_STAGES[qrScanResult.item.type].map(
                        (stage, index) => (
                          <div
                            key={stage}
                            className={`flex items-center gap-2 p-2 rounded text-sm ${getStageStatusColor(
                              stage,
                              qrScanResult.item.currentStage,
                              qrScanResult.item.type
                            )}`}
                          >
                            {getStageIcon(
                              stage,
                              qrScanResult.item.currentStage,
                              qrScanResult.item.type
                            )}
                            <span>
                              {index + 1}. {stage}
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => setShowUpdateModal(true)}
                      disabled={qrScanResult.item.status === "dispatched"}
                      className={`flex-1 px-4 py-2 rounded text-sm font-medium ${
                        qrScanResult.item.status === "dispatched"
                          ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                          : "bg-blue-500 text-white hover:bg-blue-600"
                      }`}
                    >
                      Update Stage
                    </button>
                    <button
                      onClick={resetScanner}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded text-sm font-medium hover:bg-gray-300"
                    >
                      Scan Another
                    </button>
                  </div>
                </div>

                {/* Recent Activity */}
                {qrScanResult.item.logs.length > 0 && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">Recent Activity</h4>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {qrScanResult.item.logs
                        .slice(-3)
                        .reverse()
                        .map((log) => (
                          <div
                            key={log.id}
                            className="text-xs text-gray-600 border-l-2 border-blue-200 pl-2"
                          >
                            <div className="font-medium">{log.stage}</div>
                            <div>
                              {log.userName} •{" "}
                              {new Date(log.timestamp).toLocaleString()}
                            </div>
                            {log.notes && (
                              <div className="text-gray-500">"{log.notes}"</div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!scannerActive && !qrScanResult && (
              <div className="text-center py-8">
                <ExclamationTriangleIcon className="w-12 h-12 text-yellow-500 mx-auto mb-2" />
                <p className="text-gray-600">No item found with this QR code</p>
                <button
                  onClick={resetScanner}
                  className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Try Again
                </button>
              </div>
            )}
          </div>
        </Dialog.Panel>
      </div>

      {/* Update Stage Modal */}
      <Dialog
        open={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md w-full rounded-xl bg-white p-6">
            <div className="flex justify-between items-center mb-4">
              <Dialog.Title className="text-lg font-semibold">
                Update Item Stage
              </Dialog.Title>
              <button
                onClick={() => setShowUpdateModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {qrScanResult && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm">
                    <strong>Item:</strong> {qrScanResult.item.type}
                  </p>
                  <p className="text-sm">
                    <strong>Current Stage:</strong>{" "}
                    {qrScanResult.item.currentStage}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Next Stage
                  </label>
                  <select
                    value={selectedNextStage}
                    onChange={(e) => setSelectedNextStage(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select next stage...</option>
                    {getAvailableNextStages(
                      qrScanResult.item.currentStage,
                      qrScanResult.item.type
                    ).map((stage) => (
                      <option key={stage} value={stage}>
                        {stage}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={updateNotes}
                    onChange={(e) => setUpdateNotes(e.target.value)}
                    rows={3}
                    placeholder="Add any notes about this stage completion..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Photo (Optional)
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      <CameraIcon className="w-4 h-4 mr-2" />
                      {updatePhoto ? "Change Photo" : "Add Photo"}
                    </button>
                    {updatePhoto && (
                      <span className="text-sm text-green-600">
                        Photo selected: {updatePhoto.name}
                      </span>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoSelect}
                    className="hidden"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowUpdateModal(false)}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateStage}
                    disabled={!selectedNextStage}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md ${
                      selectedNextStage
                        ? "bg-green-500 text-white hover:bg-green-600"
                        : "bg-gray-200 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    Update Stage
                  </button>
                </div>
              </div>
            )}
          </Dialog.Panel>
        </div>
      </Dialog>
    </Dialog>
  );
};

export default QRScanner;
